import { can } from '@selfon/domain/browser';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth/AuthContext';
import { principalOf } from '../components/Layout';
import { Alert, Empty, Loading, PageHeader, useLoad } from '../components/ui';
import { pct, SESSION_LABEL, thisMonthKst, time, todayKst, won } from '../format';

const TONE = { SCHEDULED: 'info', COMPLETED: 'ok', NO_SHOW: 'danger', CANCELLED: 'muted' } as const;

/**
 * 원장님·선생님 첫 화면 "오늘": 오늘 수업, 처리할 일, 이달 숫자(참고: 클래스업 대시보드, docs/UI_REFERENCES.md).
 * 숫자는 각 화면과 같은 API 로 계산해 화면 사이 값이 어긋나지 않는다.
 */
export function Home() {
  const { me } = useAuth();
  const m = me!;
  const p = principalOf(m);
  const centerId = m.centerId!;
  const month = thisMonthKst();
  const today = todayKst();
  const isTherapist = m.role === 'THERAPIST';

  const q = useLoad(async () => {
    const [sessions, clients, staff, billing, notices] = await Promise.all([
      can(p, 'session:read') ? api.sessions(centerId, month) : null,
      can(p, 'client:read') || can(p, 'task:read') ? api.clients(centerId) : null,
      can(p, 'session:read') ? api.staff(centerId) : null,
      !isTherapist && can(p, 'ledger:read') ? api.billing(centerId, month).catch(() => null) : null, // 원장님만(선생님 첫 화면은 수업·과제 중심). 실패해도 오늘 일정은 보여 준다
      can(p, 'notice:read') ? api.notices().catch(() => null) : null,
    ]);
    // 선생님: 담당 이용자의 제출된(확인 필요) 과제 수
    let toReview = 0;
    if (isTherapist && clients && can(p, 'task:evaluate')) {
      const lists = await Promise.all(clients.clients.slice(0, 30).map((c) => api.tasks(centerId, c.id).catch(() => ({ tasks: [] }))));
      toReview = lists.reduce((n, l) => n + l.tasks.filter((t) => t.status === 'SUBMITTED').length, 0);
    }
    return { sessions: sessions?.sessions ?? [], clients: clients?.clients ?? [], staff: staff?.staff ?? [], summary: billing?.summary ?? null, notices: notices?.notices ?? [], toReview };
  }, [centerId, month]);

  const d = q.data;
  const now = Date.now();
  const clientName = new Map((d?.clients ?? []).map((c) => [c.id, c.displayName]));
  const staffName = new Map((d?.staff ?? []).map((s) => [s.userId, s.name]));
  const todays = (d?.sessions ?? [])
    .filter((s) => s.startAt.slice(0, 10) === today && (!isTherapist || s.therapistId === m.userId))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
  const ended = (s: { endAt: string }) => new Date(s.endAt).getTime() < now;
  const unmarked = todays.filter((s) => s.status === 'SCHEDULED' && ended(s));
  const remaining = todays.filter((s) => s.status === 'SCHEDULED' && !ended(s));
  const unread = (d?.notices ?? []).filter((n) => n.read === false).length;
  const sum = d?.summary;

  const todo = [
    unmarked.length > 0 && { to: '/sessions', text: `끝난 수업 ${unmarked.length}건의 출결 정하기` },
    isTherapist && d && d.toReview > 0 && { to: '/tasks', text: `제출된 과제 ${d.toReview}개 평가하기` },
    sum && sum.outstanding > 0 && { to: '/billing', text: `이달 미수금 ${won(sum.outstanding)} 확인하기` },
    unread > 0 && { to: '/notices', text: `안 읽은 공지 ${unread}건 읽기` },
  ].filter(Boolean) as Array<{ to: string; text: string }>;

  const dateLabel = new Date().toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric', weekday: 'long' });

  return (
    <>
      <PageHeader title={`${m.name ? `${m.name}님, ` : ''}오늘 할 일`} sub={dateLabel}
        actions={can(p, 'session:write') && <Link to="/sessions" className="btn btn-primary">일정 등록</Link>} />
      {q.error && <Alert>{q.error} <button type="button" className="link" onClick={q.reload}>다시 시도</button></Alert>}
      {q.loading && !d && <Loading what="오늘 할 일을 모으는 중" />}

      {d && <>
        <div className="kpis home-kpis">
          <Stat to="/sessions" label={isTherapist ? '오늘 내 수업' : '오늘 수업'} value={`${todays.length}건`}
            sub={remaining.length ? `남은 수업 ${remaining.length}건` : todays.length ? '오늘 수업이 모두 끝났어요' : '오늘은 수업이 없어요'} />
          <Stat to="/sessions" label="출결 미처리" value={`${unmarked.length}건`} warn={unmarked.length > 0}
            sub={unmarked.length ? '끝난 수업의 출석·결석을 정해 주세요' : '모두 처리했어요'} />
          {isTherapist && can(p, 'task:evaluate') && (
            <Stat to="/tasks" label="확인할 과제" value={`${d.toReview}개`} warn={d.toReview > 0} sub={d.toReview ? '아이들이 낸 결과물을 평가해 주세요' : '새로 제출된 과제가 없어요'} />
          )}
          {sum && <Stat to="/billing" label="이달 미수금" value={won(sum.outstanding)} warn={sum.outstanding > 0} sub={`수납률 ${pct(sum.collectionRate)} · 완납 ${sum.fullyPaidClients}/${sum.clients}명`} />}
          {can(p, 'notice:read') && <Stat to="/notices" label="안 읽은 공지" value={`${unread}건`} warn={unread > 0} sub={unread ? '본사 공지를 확인하세요' : '새 공지가 없어요'} />}
        </div>

        <div className="home-grid">
          <section className="card stack" aria-labelledby="home-today">
            <div className="panel-head"><h2 id="home-today">오늘 일정</h2><Link to="/sessions" className="small">달력 보기</Link></div>
            {todays.length === 0
              ? <Empty>오늘은 예정된 수업이 없어요.{can(p, 'session:write') && ' 일정·출결에서 새 수업을 잡을 수 있어요.'}</Empty>
              : (
                <ul className="home-agenda">
                  {todays.map((s) => {
                    const late = s.status === 'SCHEDULED' && ended(s);
                    return (
                      <li key={s.id} className={ended(s) ? 'past' : undefined}>
                        <time dateTime={s.startAt}>{time(s.startAt)}</time>
                        <div><strong>{clientName.get(s.clientId) ?? '이용자'}</strong><span className="muted small">{s.program}{!isTherapist && staffName.get(s.therapistId) ? ` · ${staffName.get(s.therapistId)}` : ''}</span></div>
                        <span className={`chip chip-${late ? 'warn' : TONE[s.status]}`}>{late ? '출결 필요' : SESSION_LABEL[s.status]}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
          </section>

          <section className="card stack" aria-labelledby="home-todo">
            <h2 id="home-todo">처리할 일</h2>
            {todo.length === 0
              ? <p className="muted">지금 처리할 일이 없어요.</p>
              : <ul className="home-todo">{todo.map((t) => <li key={t.text}><Link to={t.to}>{t.text}<span aria-hidden="true">›</span></Link></li>)}</ul>}
          </section>
        </div>
      </>}
    </>
  );
}

function Stat({ to, label, value, sub, warn }: { to: string; label: string; value: string; sub: string; warn?: boolean }) {
  return (
    <Link to={to} className={`kpi kpi-link${warn ? ' kpi-warn' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </Link>
  );
}
